import { RefObject, useRef } from "react";
import type { ChangeEvent } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import FieldGridContainer from "../fieldGridContainer";

export type ControlledColorFieldProps<
  TFieldValues extends FieldValues = FieldValues,
> = {
  control?: Control<TFieldValues>;
  isGridItem?: boolean;
  label: string;
  id: string;
  name: Path<TFieldValues>;
  helperText?: string;
  errorMessage?: string;
  /** Show a control to unset the value (optional brand colors). */
  clearable?: boolean;
};

const normalizeRaw = (value: unknown): string => {
  if (value == null || typeof value !== "string") return "";
  return value.startsWith("#") ? value.slice(1) : value;
};

const isValidSixDigitHex = (raw: string): boolean =>
  /^[0-9A-Fa-f]{6}$/.test(raw);

/** Outlined clickable control: swatch + value + picker affordance, full-area hit target. */
function ColorPickTarget(props: {
  id: string;
  colorInputRef: RefObject<HTMLInputElement | null>;
  validationMessage?: string | null;
  colorInputHex: string;
  onColorChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Visual swatch (CSS color string). */
  swatchCss: string;
  valueLabel: string;
  placeholder?: string;
}) {
  const {
    id,
    colorInputRef,
    validationMessage,
    colorInputHex,
    onColorChange,
    swatchCss,
    valueLabel,
    placeholder,
  } = props;
  const theme = useTheme();
  const borderBase =
    theme.palette.mode === "light"
      ? "rgba(0, 0, 0, 0.23)"
      : "rgba(255, 255, 255, 0.23)";

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: 40,
        borderRadius: 1,
        border: "1px solid",
        borderColor: validationMessage ? "error.main" : borderBase,
        bgcolor: "background.paper",
        transition: theme.transitions.create(
          ["border-color", "background-color", "box-shadow"],
          { duration: theme.transitions.duration.shorter },
        ),
        "&:hover": {
          borderColor: validationMessage ? "error.dark" : "text.primary",
          bgcolor: "action.hover",
        },
        "&:focus-within": {
          borderColor: validationMessage ? "error.main" : "primary.main",
          boxShadow: `0 0 0 1px ${
            validationMessage
              ? theme.palette.error.main
              : theme.palette.primary.main
          }`,
        },
      }}
    >
      <Box
        component="input"
        ref={colorInputRef}
        type="color"
        id={id}
        value={colorInputHex}
        onChange={onColorChange}
        aria-label="Open color picker"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          cursor: "pointer",
          zIndex: 1,
          p: 0,
          border: "none",
          "&::-webkit-color-swatch-wrapper": { padding: 0 },
        }}
        data-testid={`${id}-color-picker`}
      />
      <Box
        component="span"
        aria-hidden
        sx={{
          position: "relative",
          zIndex: 0,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.25,
          py: 1,
          minHeight: 40,
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            borderRadius: 0.75,
            bgcolor: swatchCss,
            border: `1px solid ${borderBase}`,
            boxShadow: theme.shadows[1],
          }}
        />
        <Typography
          variant="body2"
          fontFamily="monospace"
          fontWeight={500}
          color={placeholder ? "text.secondary" : "text.primary"}
          sx={{ flex: 1, minWidth: 0 }}
        >
          {valueLabel || placeholder || "—"}
        </Typography>
        <PaletteOutlinedIcon
          sx={{ fontSize: 22, color: "text.secondary", flexShrink: 0 }}
        />
      </Box>
    </Box>
  );
}

function ControlledColorField<TFieldValues extends FieldValues = FieldValues>(
  props: ControlledColorFieldProps<TFieldValues>,
) {
  const {
    control,
    isGridItem = true,
    label,
    id,
    name,
    errorMessage,
    helperText = "",
    clearable = false,
  } = props;

  const resolvedControl = control as unknown as Control<FieldValues>;
  const resolvedName = name as string;

  const colorInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  const body = ({
    field,
    fieldState,
  }: {
    field: Record<string, any>;
    fieldState: { error?: { message?: string } };
  }) => {
    const rawUpper = normalizeRaw(field.value).toUpperCase();
    const validHex = isValidSixDigitHex(rawUpper);
    const validationMessage =
      errorMessage ??
      fieldState.error?.message;
    const hint = helperText;
    const colorInputHex = `#${validHex ? rawUpper : "808080"}`;

    const onColorChange = (e: ChangeEvent<HTMLInputElement>) => {
      field.onChange(e.target.value.replace("#", "").toUpperCase());
    };

    const showUnsetActions = clearable && !validHex;
    const emptySwatch =
      theme.palette.grey[theme.palette.mode === "light" ? 300 : 800];

    return (
      <Box sx={{ position: "relative", width: "100%" }}>
        <Typography
          component="label"
          htmlFor={id}
          variant="subtitle2"
          color="text.secondary"
          sx={{ display: "block", mb: 0.75 }}
        >
          {label}
        </Typography>
        <Box display="flex" alignItems="stretch" gap={0.75} flexWrap="nowrap">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ColorPickTarget
              id={id}
              colorInputRef={colorInputRef}
              validationMessage={validationMessage}
              colorInputHex={colorInputHex}
              onColorChange={onColorChange}
              swatchCss={validHex ? `#${rawUpper}` : emptySwatch}
              valueLabel={validHex ? `#${rawUpper}` : ""}
              placeholder={
                showUnsetActions
                  ? "Click to choose a color"
                  : !validHex
                    ? "Pick a color"
                    : undefined
              }
            />
          </Box>

          {clearable && validHex ? (
            <Tooltip title="Clear color">
              <IconButton
                type="button"
                size="small"
                aria-label="Clear color"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  field.onChange("");
                }}
                sx={{
                  alignSelf: "center",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  flexShrink: 0,
                  "&:hover": {
                    borderColor: "text.secondary",
                    bgcolor: "action.hover",
                  },
                }}
                data-testid={`${id}-clear-color`}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
        {(validationMessage || hint) && (
          <Typography
            variant="caption"
            color={validationMessage ? "error" : "text.secondary"}
            sx={{ mt: 0.5, display: "block" }}
          >
            {validationMessage ?? hint}
          </Typography>
        )}
      </Box>
    );
  };

  const controller = (
    <Controller
      name={resolvedName}
      control={resolvedControl}
      render={({ field, fieldState }) => body({ field, fieldState })}
    />
  );

  if (isGridItem) {
    return <FieldGridContainer>{controller}</FieldGridContainer>;
  }

  return controller;
}

export default ControlledColorField;
